import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { socialApi } from '@/lib/socialApi'
import { describeError } from '@/lib/auth'
import { avatarUrl, guildIconUrl } from '@/lib/avatar'
import { guildColor, guildInitials, plural } from '@/lib/guildUi'
import { openProfile } from '@/store/contextMenu'
import { useGuildStore } from '@/store/guilds'
import { displayName, useSocialStore } from '@/store/social'
import { cx } from '@/lib/cx'
import type { Guild, User } from '@/types/api'
import { Avatar } from '../Avatar'
import { StyledName } from '../StyledName'
import { ConfirmModal } from '../guild/ConfirmModal'
import chatStyles from '../ChatView.module.css'
import styles from './DMChat.module.css'

/**
 * Начало личной беседы, как в Discord: аватар, имя и @ник, «Это начало истории…», общие серверы
 * и кнопки дружбы и блокировки. Удалить из друзей и заблокировать — через подтверждение.
 */
export function DMWelcome({ user }: { user: User }) {
  const relationship = useSocialStore((s) => s.relationships[user.id])
  const { requestFriend, acceptFriend, removeRelationship, block } = useSocialStore()
  const guilds = useGuildStore((s) => s.guilds)
  // тот же запрос, что у панели профиля справа: данные берутся из общего кэша
  const profile = useQuery({ queryKey: ['profile', user.id], queryFn: () => socialApi.profile(user.id), staleTime: 60_000 })
  const [confirm, setConfirm] = useState<'unfriend' | 'block' | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const name = displayName(user)
  const mutual = (profile.data?.mutual_guild_ids ?? []).map((id) => guilds[id]).filter((g): g is Guild => Boolean(g))
  const type = relationship?.type

  const run = (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    void fn()
      .catch((err: unknown) => setError(describeError(err).message))
      .finally(() => setBusy(false))
  }

  return (
    <div className={chatStyles.welcome}>
      <Avatar initial={name.slice(0, 1).toUpperCase()} color="#5865F2" size={80} src={avatarUrl(user.id, user.avatar)} decoration={user.avatar_decoration_data} className={chatStyles.welcomeAvatar} />
      <h2 className={chatStyles.welcomeTitle}><StyledName name={name} style={user.display_name_styles} /></h2>
      <p className={styles.welcomeUsername}>@{user.username}</p>
      <p className={chatStyles.welcomeText}>Это начало истории ваших личных сообщений с <strong>@{user.username}</strong>.</p>

      <div className={styles.welcomeRow}>
        {mutual.length ? (
          <button type="button" className={styles.welcomeMutual} onClick={() => openProfile(user.id, { user, tab: 'guilds' })}>
            <span className={styles.welcomeFaces}>
              {mutual.slice(0, 3).map((g) => {
                const icon = guildIconUrl(g.id, g.icon)
                return icon
                  ? <img key={g.id} src={icon} alt="" className={styles.welcomeFace} />
                  : <span key={g.id} className={cx(styles.welcomeFace, styles.welcomeFaceText)} style={{ background: guildColor(g.id) }}>{guildInitials(g.name)}</span>
              })}
            </span>
            {mutual.length} {plural(mutual.length, 'общий сервер', 'общих сервера', 'общих серверов')}
          </button>
        ) : null}
        {user.bot ? null : (
          <>
            {type === 1 ? (
              <button type="button" className={styles.welcomeButton} disabled={busy} onClick={() => setConfirm('unfriend')}>Удалить из друзей</button>
            ) : type === 3 ? (
              <button type="button" className={cx(styles.welcomeButton, styles.welcomePrimary)} disabled={busy} onClick={() => run(() => acceptFriend(user.id))}>Принять заявку</button>
            ) : type === 4 ? (
              <button type="button" className={styles.welcomeButton} disabled>Заявка отправлена</button>
            ) : type === 2 ? null : (
              <button type="button" className={cx(styles.welcomeButton, styles.welcomePrimary)} disabled={busy} onClick={() => run(() => requestFriend(user.username))}>Добавить в друзья</button>
            )}
            {type === 2 ? (
              <button type="button" className={styles.welcomeButton} disabled={busy} onClick={() => run(() => removeRelationship(user.id))}>Разблокировать</button>
            ) : (
              <button type="button" className={styles.welcomeButton} disabled={busy} onClick={() => setConfirm('block')}>Заблокировать</button>
            )}
          </>
        )}
      </div>
      {error ? <p className={styles.welcomeError} role="alert">{error}</p> : null}

      {confirm === 'unfriend' ? (
        <ConfirmModal
          title={`Удалить «${name}» из друзей?`}
          text="Переписка останется. Снова дружить — только через новую заявку."
          confirmLabel="Удалить из друзей"
          onConfirm={async () => {
            await removeRelationship(user.id)
            setConfirm(null)
          }}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
      {confirm === 'block' ? (
        <ConfirmModal
          title={`Заблокировать «${name}»?`}
          text="Писать друг другу станет нельзя, из друзей человек пропадёт. Блокировку можно снять."
          confirmLabel="Заблокировать"
          onConfirm={async () => {
            await block(user.id)
            setConfirm(null)
          }}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </div>
  )
}
